import { useEffect, useRef, useState } from 'react';
import { useSelectedSection, useViewMode } from '../../store/app/appStore';
import { idleManager } from '../../core/services/managers/IdleManager';
import { useTrueMobile } from './useTrueMobile';

// Base of the IdleManager process id; the counter suffix keeps every registration
// unique. IdleManager keys processes in a Map, so two live instances sharing one id
// overwrite each other's entry — the second registration replaces the first, and the
// first one's cleanup then stops the wrong listener, leaving its own attached for good.
const IDLE_PROCESS_ID = 'nav-footer-hide-on-scroll';
let registrationCount = 0;

const SCROLL_DIRECTION_THRESHOLD = 10;
const TOP_OFFSET_THRESHOLD = 50;
const BOTTOM_OFFSET_THRESHOLD = 15;
// Idle delay after which the bars come back on their own, so they are never left
// hidden once the user stops scrolling.
const SCROLL_IDLE_MS = 500;

/**
 * Hides the bottom Navigation/StatusBar on mobile while the user scrolls down, and shows
 * them again on scroll up, at either end of the scroll, or once scrolling stops.
 *
 * The bars overlay the scrolling content (see .lobbyContainer in Lobby.module.css),
 * so hiding them reveals content instead of resizing anything.
 *
 * Which element actually scrolls varies: the lobby views scroll .lobbyContainer, while
 * a single-view campaign renders a fixed overlay that scrolls one of its own children.
 * Scroll events don't bubble but they do capture, so a capture-phase listener on the
 * document sees whichever element it happens to be.
 *
 * The listener is registered with the IdleManager, so it is detached while the app is
 * idle (ingame) and reattached on wake, alongside the other idle-managed processes.
 */
export const useHideNavFooterOnScroollMobile = (): boolean => {
  const isMobile = useTrueMobile();
  const viewMode = useViewMode();
  const selectedSection = useSelectedSection();
  const [isHidden, setIsHidden] = useState(false);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const lastScrollTopRef = useRef(0);
  const lastGeometryRef = useRef('');
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const processId = `${IDLE_PROCESS_ID}#${++registrationCount}`;

    // Every view/section change starts with the bars visible — content short enough to
    // fit the screen never scrolls, so nothing would bring them back.
    setIsHidden(false);
    scrollerRef.current = null;
    lastGeometryRef.current = '';

    if (!isMobile) return;

    const handleScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      
      const { scrollTop, clientHeight, scrollHeight }= target;

      // Only the lobby's own content drives the bars. Overlays rendered as siblings of
      // the views — the settings modal, search — scroll their own panels, and hiding the
      // bars behind them is neither useful nor expected.
      if (!target.closest('[data-section-container]')) return;

      // Horizontal carousels (game rows, campaign carousel, the nav tab strip) also emit
      // scroll events; only vertical scrollers drive the bars.
      if (scrollHeight - clientHeight <= BOTTOM_OFFSET_THRESHOLD) return;

      // Restarted on every event, so it only fires once the scrolling has settled.
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => setIsHidden(false), SCROLL_IDLE_MS);

     
      // A rotation, the Android address bar and a lazy-mounting section all resize the
      // scroller, and that moves scrollTop on its own. A gesture never does, so a changed
      // geometry means this jump is layout rather than input — start from a new baseline
      // instead of measuring it, or it reads as a scroll down and hides the bars.
      const geometry = `${clientHeight}x${scrollHeight}`;

      if (scrollerRef.current !== target || lastGeometryRef.current !== geometry) {
        scrollerRef.current = target;
        lastGeometryRef.current = geometry;
        lastScrollTopRef.current = scrollTop;
        return;
      }

      const delta = scrollTop - lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;
      // At the very bottom the bars sit over the content's trailing clearance, so
      // showing them there covers it instead of leaving an empty band.
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (scrollTop <= TOP_OFFSET_THRESHOLD || distanceToBottom <= BOTTOM_OFFSET_THRESHOLD) {
        setIsHidden(false);
      } else if (delta > SCROLL_DIRECTION_THRESHOLD) {
        setIsHidden(true);
      } else if (delta < -SCROLL_DIRECTION_THRESHOLD) {
        setIsHidden(false);
      }
    };

    const start = () => {
      document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    };

    const stop = () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      // Never leave the bars hidden behind an idle screen.
      setIsHidden(false);
    };

    idleManager.registerProcess(processId, start, stop);

    return () => {
      idleManager.unregisterProcess(processId);
    };
  }, [isMobile, viewMode, selectedSection]);

  return isHidden;
};
